import React from "react";
import styled, { css } from "styled-components";
import { Avatar, Tooltip } from "@material-ui/core";

import { getAvatarColorForIdentifier } from "js/core/utilities/avatarGenerator";

export interface AvatarStackProfile {
    photoURL?: string | null;
    displayName?: string;
    email?: string;
    isCreator?: boolean;
    /** True if the user has read-only (viewer) access to the resource */
    isReadOnly?: boolean;
}

export const AVATAR_STACK_SIZE = 24;
export const AVATAR_STACK_OVERLAP = 4;
export const AVATAR_STACK_MAX_VISIBLE_AVATARS = 5;

const StyledAvatar = styled(Avatar)<{ $backgroundColor?: string; $isOverflow?: boolean; $overflowDigits?: number }>`
    &&& {
        width: ${AVATAR_STACK_SIZE}px;
        min-width: ${AVATAR_STACK_SIZE}px;
        max-width: ${AVATAR_STACK_SIZE}px;
        height: ${AVATAR_STACK_SIZE}px;
        min-height: ${AVATAR_STACK_SIZE}px;
        max-height: ${AVATAR_STACK_SIZE}px;
        margin-left: -${AVATAR_STACK_OVERLAP}px;
        outline: ${({ $isOverflow }) => $isOverflow ? "none" : "1px solid white"};
        border: ${({ $isOverflow }) => $isOverflow ? "1px solid #DDD" : "none"};
        border-radius: 50%;
        box-sizing: border-box;
        padding: 0;
        overflow: hidden;
        color: ${({ $isOverflow }) => $isOverflow ? "#666" : "white"};
        font-size: ${({ $isOverflow, $overflowDigits }) => {
        if (!$isOverflow) return "12px";
        if ($overflowDigits > 3) return "8px";
        if ($overflowDigits > 2) return "9px";
        return "11px";
    }};
        font-weight: 600;
        line-height: ${AVATAR_STACK_SIZE}px;
        box-shadow: none;
        ${({ $backgroundColor, $isOverflow }) => !$isOverflow && $backgroundColor && css`background-color: ${$backgroundColor};`}
        ${({ $isOverflow }) => $isOverflow && css`background-color: #F5F5F5;`}

        .MuiAvatar-img {
            object-fit: fill;
        }
    }
`;

const Stack = styled.div`
    display: flex;
    align-items: center;
    padding-left: ${AVATAR_STACK_OVERLAP}px;
    & > * { position: relative; }
    & > *:nth-child(1) { z-index: 1; }
    & > *:nth-child(2) { z-index: 2; }
    & > *:nth-child(3) { z-index: 3; }
    & > *:nth-child(4) { z-index: 4; }
    & > *:nth-child(5) { z-index: 5; }
    & > *:last-child { z-index: 10; }
`;

export function getCollaboratorLabel(name: string, isCreator: boolean, isReadOnly?: boolean): string {
    if (isCreator) return `${name} (Creator)`;
    if (isReadOnly) return `${name} (View Only)`;
    return name;
}

const TooltipContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const AdditionalAvatars = ({
    profiles,
}: {
    profiles: AvatarStackProfile[];
}) => {
    if (!profiles?.length) return null;

    const tooltip = (
        <TooltipContent>
            {profiles.map((p, i) => (
                <div key={p.email ?? p.displayName ?? i}>
                    {getCollaboratorLabel(p.displayName ?? p.email ?? "", !!p.isCreator, p.isReadOnly)}
                </div>
            ))}
        </TooltipContent>
    );

    return (
        <Tooltip title={tooltip} placement="bottom">
            <StyledAvatar $isOverflow $overflowDigits={String(profiles.length).length}>+{profiles.length}</StyledAvatar>
        </Tooltip>
    );
};

function getCappedVisibleAvatarBounds(minVisibleAvatars: number, maxVisibleAvatars: number) {
    const cappedMaxVisibleAvatars = Math.max(0, Math.min(AVATAR_STACK_MAX_VISIBLE_AVATARS, maxVisibleAvatars));
    const cappedMinVisibleAvatars = Math.max(0, Math.min(cappedMaxVisibleAvatars, minVisibleAvatars));

    return {
        cappedMinVisibleAvatars,
        cappedMaxVisibleAvatars,
    };
}

function getVisibleAvatarCount(profileCount: number, minVisibleAvatars: number, maxVisibleAvatars: number) {
    const { cappedMinVisibleAvatars, cappedMaxVisibleAvatars } = getCappedVisibleAvatarBounds(minVisibleAvatars, maxVisibleAvatars);

    if (profileCount <= 2) {
        return profileCount;
    }

    return Math.min(profileCount, Math.max(cappedMinVisibleAvatars, cappedMaxVisibleAvatars));
}

function getAvatarStackProfiles(profiles: AvatarStackProfile[], minVisibleAvatars: number, maxVisibleAvatars: number) {
    let visibleAvatarCount = getVisibleAvatarCount(profiles.length, minVisibleAvatars, maxVisibleAvatars);
    const overflowCount = profiles.length - visibleAvatarCount;

    if (overflowCount === 1) {
        if (visibleAvatarCount + 1 <= AVATAR_STACK_MAX_VISIBLE_AVATARS) {
            visibleAvatarCount += 1;
        } else if (visibleAvatarCount > 0) {
            visibleAvatarCount -= 1;
        }
    }

    return {
        visibleProfiles: profiles.slice(0, visibleAvatarCount),
        overflowProfiles: profiles.slice(visibleAvatarCount),
    };
}

export function getMaxVisibleAvatarsForWidth(width: number | null, minVisibleAvatars = 1, maxVisibleAvatars = AVATAR_STACK_MAX_VISIBLE_AVATARS) {
    if (!width) {
        return maxVisibleAvatars;
    }

    const visibleAvatarWidth = AVATAR_STACK_SIZE - AVATAR_STACK_OVERLAP;
    const visibleAvatarCount = Math.floor((width - AVATAR_STACK_SIZE) / visibleAvatarWidth) + 1;

    return Math.max(minVisibleAvatars, Math.min(maxVisibleAvatars, visibleAvatarCount));
}

export default function AvatarStack({
    profiles,
    showSingleAvatar = false,
    minVisibleAvatars = 1,
    maxVisibleAvatars = AVATAR_STACK_MAX_VISIBLE_AVATARS,
}: {
    profiles: AvatarStackProfile[];
    showSingleAvatar?: boolean;
    minVisibleAvatars?: number;
    maxVisibleAvatars?: number;
}) {
    if (!profiles?.length || (profiles.length === 1 && !showSingleAvatar)) return null;

    const { visibleProfiles, overflowProfiles } = getAvatarStackProfiles(profiles, minVisibleAvatars, maxVisibleAvatars);

    return (
        <Stack>
            {visibleProfiles.map(({ displayName, email, photoURL, isCreator, isReadOnly }, idx) => {
                const name = displayName ?? email ?? "";
                const hasPhoto = !!photoURL?.trim();

                const avatarComponent = hasPhoto ? (<StyledAvatar
                    alt={name.toUpperCase()}
                    src={photoURL}
                />) : (<StyledAvatar
                    alt={name.toUpperCase()}
                    $backgroundColor={getAvatarColorForIdentifier(name)}
                >{name.charAt(0).toUpperCase()}</StyledAvatar>);

                return (
                    <Tooltip
                        key={email ?? displayName ?? idx}
                        title={getCollaboratorLabel(name, !!isCreator, isReadOnly)}
                        placement="bottom"
                    >
                        {avatarComponent}
                    </Tooltip>
                );
            })}
            <AdditionalAvatars profiles={overflowProfiles} />
        </Stack>
    );
}
